/**
 * Leena-2 research preview gating.
 *
 * The Leena-2 product model version (`LEENA2_PRODUCT_MODEL_VERSION`) is a
 * research preview that only produces Net Impact results. When a company's
 * selected version is Leena-2, every other lens (DMA, Financial Effects, SDG,
 * EU Taxonomy, SFDR/PAI) is replaced with a placeholder styled like the
 * "no access" empty state, and the lens selector marks those lenses as
 * unavailable. This applies to the company view only.
 */
import { ImpactLensType } from '@upright/common/impact-lenses/types';
import { BouColor } from '@upright/ui/common/generic/global/bou-colors';
import { NoContentBox } from '@upright/ui/common/generic/layout/no-content-box';
import { LensDataAccessDetails, LensDataAvailability } from '@upright/ui/common/upright/core/impact-lens-profile/types';
import { isLeena2ProductModelVersion } from '@upright/ui/common/upright/model/output/leena2-version-marker';
import { useSelectedModelVersion } from '@upright/ui/common/upright/model/output/releases/hooks';
import styled from 'styled-components';

/** Whether the currently selected product model version is the Leena-2 research preview. */
export function useIsLeena2ResearchPreview(): boolean {
  return isLeena2ProductModelVersion(useSelectedModelVersion());
}

/**
 * Returns a copy of the lens access map with every non-Net-Impact lens marked
 * unavailable, so the lens selector shows the standard not-available indicator
 * for the lenses the Leena-2 research preview does not produce.
 */
export function applyLeena2LensAvailabilityOverride(
  map: Map<ImpactLensType, LensDataAccessDetails> | undefined,
): Map<ImpactLensType, LensDataAccessDetails> | undefined {
  if (!map) return map;
  const result = new Map(map);
  for (const [lens, details] of map) {
    if (lens === ImpactLensType.UPRIGHT_NET_IMPACT) continue;
    result.set(lens, { ...details, availability: LensDataAvailability.UNAVAILABLE });
  }
  return result;
}

/**
 * Placeholder shown in place of a non-Net-Impact lens's content when the
 * selected version is the Leena-2 research preview. Reuses the "no access"
 * empty-state box styling.
 */
export function Leena2ResearchPreviewEmptyState() {
  return (
    <Wrapper>
      <NoContentBox title="Research preview" topBlockColor={BouColor.IMPACT_NEGATIVE_PURPLE}>
        Beta preview results currently not available for this dataset.
      </NoContentBox>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  padding-top: var(--space__4xl);
`;
